/**
 * AffordabilityCalc — Full-page affordability calculator with auto-recalc,
 * donut breakdown, and scenario comparison.
 *
 * @param {Object} props
 * @param {number} [props.defaultPrice] - Optional pre-filled home price
 * @returns {JSX.Element}
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { calculateAffordability, compareScenarios } from '../../agents/affordabilityAgent';
import './AffordabilityCalc.css';

const DEFAULT_FORM = {
  home_price: 400000,
  down_payment_percent: 20,
  interest_rate: 6.5,
  loan_term_years: 30,
  annual_property_tax_rate: 1.25,
  annual_insurance: 1500,
  monthly_hoa: 0,
  annual_pmi_rate: 0.5,
};

const PRESET_SCENARIOS = [
  { label: 'Conservative (30% / 5.5%)', down_payment_percent: 30, interest_rate: 5.5, loan_term_years: 30 },
  { label: 'Standard (20% / 6.5%)', down_payment_percent: 20, interest_rate: 6.5, loan_term_years: 30 },
  { label: 'Aggressive (10% / 7.5%)', down_payment_percent: 10, interest_rate: 7.5, loan_term_years: 30 },
];

const DONUT_COLORS = {
  principal_interest: '#c0583e',
  taxes: '#0f172a',
  insurance: '#6366f1',
  hoa: '#f59e0b',
  pmi: '#10b981',
};

const formatCurrency = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

const AffordabilityCalc = ({ defaultPrice }) => {
  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...(defaultPrice ? { home_price: defaultPrice } : {}),
  }));
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  // Debounced auto-calculate
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runCalculate();
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const runCalculate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateAffordability(form);
      setResult(data);
    } catch (err) {
      console.error('Affordability calculation failed:', err);
      setError(err.message || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const runCompare = async () => {
    setError(null);
    try {
      const data = await compareScenarios({
        home_price: form.home_price,
        scenarios: PRESET_SCENARIOS.map((s) => ({
          down_payment_percent: s.down_payment_percent,
          interest_rate: s.interest_rate,
          loan_term_years: s.loan_term_years,
          annual_property_tax_rate: form.annual_property_tax_rate,
          annual_insurance: form.annual_insurance,
          monthly_hoa: form.monthly_hoa,
          annual_pmi_rate: form.annual_pmi_rate,
        })),
      });
      setScenarios(Array.isArray(data) ? data : data?.scenarios || []);
    } catch (err) {
      console.error('Scenario comparison failed:', err);
      setError(err.message || 'Comparison failed');
    }
  };

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Build donut segments
  const donutSegments = (() => {
    if (!result?.breakdown) return [];
    const b = result.breakdown;
    const parts = [
      { key: 'principal_interest', label: 'P & I', value: b.principal_and_interest || 0 },
      { key: 'taxes', label: 'Taxes', value: b.property_tax || 0 },
      { key: 'insurance', label: 'Insurance', value: b.insurance || 0 },
      { key: 'hoa', label: 'HOA', value: b.hoa || 0 },
      { key: 'pmi', label: 'PMI', value: b.pmi || 0 },
    ].filter((p) => p.value > 0);
    const total = parts.reduce((s, p) => s + p.value, 0);
    let cumulative = 0;
    return parts.map((p) => {
      const pct = (p.value / total) * 100;
      const offset = cumulative;
      cumulative += pct;
      return { ...p, pct, offset, color: DONUT_COLORS[p.key] || '#ccc' };
    });
  })();

  // Find lowest monthly in scenarios
  const lowestMonthly = scenarios
    ? Math.min(...scenarios.map((s) => s.total_monthly || Infinity))
    : null;

  return (
    <div className="affordability-calc">
      {/* ── Left: Input Form ────────────────────────── */}
      <form
        className="affordability-form"
        onSubmit={(e) => { e.preventDefault(); runCalculate(); }}
      >
        <h2 className="affordability-title">Affordability Calculator</h2>

        <label className="affordability-label">
          Home Price
          <div className="affordability-input-wrap">
            <span className="affordability-prefix">$</span>
            <input
              id="input-home-price"
              type="number"
              className="affordability-input"
              value={form.home_price}
              onChange={(e) => update('home_price', Number(e.target.value))}
              min={0}
              step={5000}
            />
          </div>
        </label>

        <label className="affordability-label">
          Down Payment
          <input
            id="input-down-payment"
            type="range"
            className="affordability-slider"
            min={0}
            max={50}
            step={1}
            value={form.down_payment_percent}
            onChange={(e) => update('down_payment_percent', Number(e.target.value))}
            aria-valuetext={`${form.down_payment_percent}%`}
          />
          <output htmlFor="input-down-payment" aria-live="polite" className="affordability-output">
            {form.down_payment_percent}%
          </output>
        </label>

        <label className="affordability-label">
          Interest Rate
          <input
            id="input-interest-rate"
            type="range"
            className="affordability-slider"
            min={2}
            max={12}
            step={0.1}
            value={form.interest_rate}
            onChange={(e) => update('interest_rate', Number(e.target.value))}
            aria-valuetext={`${form.interest_rate}%`}
          />
          <output htmlFor="input-interest-rate" aria-live="polite" className="affordability-output">
            {form.interest_rate}%
          </output>
        </label>

        <fieldset className="affordability-fieldset">
          <legend>Loan Term</legend>
          <div className="affordability-radio-group">
            {[15, 30].map((yr) => (
              <label
                key={yr}
                className={`affordability-radio ${
                  form.loan_term_years === yr ? 'affordability-radio-active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="loan_term"
                  value={yr}
                  checked={form.loan_term_years === yr}
                  onChange={() => update('loan_term_years', yr)}
                />
                {yr} yr
              </label>
            ))}
          </div>
        </fieldset>

        <label className="affordability-label">
          Property Tax Rate
          <input
            id="input-tax-rate"
            type="range"
            className="affordability-slider"
            min={0}
            max={4}
            step={0.05}
            value={form.annual_property_tax_rate}
            onChange={(e) => update('annual_property_tax_rate', Number(e.target.value))}
            aria-valuetext={`${form.annual_property_tax_rate}%`}
          />
          <output htmlFor="input-tax-rate" aria-live="polite" className="affordability-output">
            {form.annual_property_tax_rate}%
          </output>
        </label>

        <label className="affordability-label">
          Annual Insurance
          <div className="affordability-input-wrap">
            <span className="affordability-prefix">$</span>
            <input
              id="input-annual-insurance"
              type="number"
              className="affordability-input"
              value={form.annual_insurance}
              onChange={(e) => update('annual_insurance', Number(e.target.value))}
              min={0}
              step={100}
            />
          </div>
        </label>

        <label className="affordability-label">
          Monthly HOA
          <div className="affordability-input-wrap">
            <span className="affordability-prefix">$</span>
            <input
              id="input-monthly-hoa"
              type="number"
              className="affordability-input"
              value={form.monthly_hoa}
              onChange={(e) => update('monthly_hoa', Number(e.target.value))}
              min={0}
              step={25}
            />
          </div>
        </label>

        <button
          type="submit"
          className="affordability-calc-btn"
          disabled={loading}
        >
          {loading ? 'Calculating…' : 'Calculate'}
        </button>
      </form>

      {/* ── Right: Results Panel ────────────────────── */}
      <div className="affordability-results">
        {error && (
          <div className="affordability-error">
            <span>⚠</span> {error}
          </div>
        )}

        {!result && !error && (
          <div className="affordability-empty">
            <div className="affordability-empty-icon">🏠</div>
            <p>Adjust the inputs to see your estimated monthly payment</p>
          </div>
        )}

        {result && (
          <>
            {/* Monthly payment card */}
            <div className="monthly-payment-card">
              <p className="monthly-payment-label">Estimated Monthly Payment</p>
              <p className="monthly-payment-amount">
                {formatCurrency(result.total_monthly)}
              </p>
              {result.breakdown && (
                <div className="monthly-payment-breakdown">
                  <span>P&I: {formatCurrency(result.breakdown.principal_and_interest)}</span>
                  <span>Tax: {formatCurrency(result.breakdown.property_tax)}</span>
                  <span>Ins: {formatCurrency(result.breakdown.insurance)}</span>
                  {result.breakdown.hoa > 0 && (
                    <span>HOA: {formatCurrency(result.breakdown.hoa)}</span>
                  )}
                  {result.breakdown.pmi > 0 && (
                    <span>PMI: {formatCurrency(result.breakdown.pmi)}</span>
                  )}
                </div>
              )}
            </div>

            {/* Loan summary */}
            <div className="loan-summary">
              <h3>Loan Summary</h3>
              <div className="loan-summary-grid">
                <div className="loan-summary-item">
                  <span className="loan-summary-label">Loan Amount</span>
                  <span className="loan-summary-value">
                    {formatCurrency(result.loan_amount)}
                  </span>
                </div>
                <div className="loan-summary-item">
                  <span className="loan-summary-label">Down Payment</span>
                  <span className="loan-summary-value">
                    {formatCurrency(result.down_payment_amount)}
                  </span>
                </div>
                <div className="loan-summary-item">
                  <span className="loan-summary-label">Total Interest</span>
                  <span className="loan-summary-value">
                    {formatCurrency(result.total_interest_lifetime)}
                  </span>
                </div>
                <div className="loan-summary-item">
                  <span className="loan-summary-label">Total Cost</span>
                  <span className="loan-summary-value">
                    {formatCurrency(result.total_cost_lifetime)}
                  </span>
                </div>
              </div>
            </div>

            {/* Donut chart */}
            {donutSegments.length > 0 && (
              <div className="payment-breakdown">
                <h3>Payment Breakdown</h3>
                <div className="payment-donut-wrap">
                  <svg
                    viewBox="0 0 42 42"
                    className="payment-donut-svg"
                    role="img"
                    aria-labelledby="donut-title"
                  >
                    <title id="donut-title">Monthly Payment Breakdown</title>
                    {donutSegments.map((seg) => (
                      <circle
                        key={seg.key}
                        cx="21"
                        cy="21"
                        r="15.91549"
                        fill="transparent"
                        stroke={seg.color}
                        strokeWidth="5"
                        strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
                        strokeDashoffset={-seg.offset}
                      />
                    ))}
                  </svg>
                  <ul className="payment-donut-legend">
                    {donutSegments.map((seg) => (
                      <li key={seg.key}>
                        <span
                          className="payment-legend-dot"
                          style={{ background: seg.color }}
                        />
                        {seg.label}: {formatCurrency(seg.value)} ({seg.pct.toFixed(1)}%)
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        {/* Scenario comparison */}
        <div className="scenario-section">
          <h3>Compare Scenarios</h3>
          <div className="scenario-presets">
            {PRESET_SCENARIOS.map((s, i) => (
              <button
                key={i}
                className="scenario-preset-btn"
                onClick={() => {
                  update('down_payment_percent', s.down_payment_percent);
                  update('interest_rate', s.interest_rate);
                  update('loan_term_years', s.loan_term_years);
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button className="affordability-compare-btn" onClick={runCompare}>
            Run Comparison
          </button>

          {scenarios && scenarios.length > 0 && (
            <table className="scenario-table">
              <caption>Scenario comparison</caption>
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Monthly</th>
                  <th>Total Interest</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((s, i) => {
                  const isBest = s.total_monthly === lowestMonthly;
                  return (
                    <tr
                      key={i}
                      className={isBest ? 'scenario-best' : ''}
                    >
                      <td>
                        {PRESET_SCENARIOS[i]?.label || `Scenario ${i + 1}`}
                        {isBest && (
                          <span className="visually-hidden"> (best option)</span>
                        )}
                      </td>
                      <td>{formatCurrency(s.total_monthly)}</td>
                      <td>{formatCurrency(s.total_interest_lifetime)}</td>
                      <td>{formatCurrency(s.total_cost_lifetime)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AffordabilityCalc;
